import { Link, useParams } from 'react-router-dom';
import {
  useCancelQuotationDetailMutation,
  useQuotationLazyQuery,
  useWholesalesQuery, // このフックが卸業者データを取得するために使われていると仮定
  useUpdateQuotationDetailCheckedMutation,
} from 'features/graphql';
import React, { useEffect, useState } from 'react';
import { Button, Col, Container, Row, Table, Form } from 'react-bootstrap';
import { parseISODateAndformatDate, sortQuotationDetails } from '../../utils/miscellaneous';
import { formatPrice } from '../../utils/priceUtils';
import { WholesaleDealPriceCells } from '../../components/WholesaleDealPriceCells';
import { WholesaleNameHeaderCells } from '../../components/WholesaleNameHeaderCells';
import { Loading } from 'components/Loading';
import { ChevronLeft } from 'react-bootstrap-icons';
import { QUOTATIONS_PATH } from 'route/paths';
import { QuotationDetail as QuotationDetailContentGql } from '../../features/graphql/apollo'; // 型名を変更して衝突を避ける
import { ConfirmModal } from '../../components/ConfirmModal';

type QuotationIdParam = {
  id: string;
};

export const QuotationDetail = () => {
  const param = useParams<QuotationIdParam>();
  const [quotationDetailQuery, { data, loading }] = useQuotationLazyQuery({
    onCompleted({ quotation }) {
      sortQuotationDetails(quotation.details);
    },
  });
  const [cancelQuotationDetail, { loading: cancelLoading }] = // medicinesLoding から cancelLoading に変更
    useCancelQuotationDetailMutation({
      onCompleted(response) {
        // responseの型を具体的にするか、anyで受ける
        const quotationId = param.id;
        if (quotationId !== undefined && response.cancelQuotationDetail) {
          // レスポンスの構造を確認
          quotationDetailQuery({ variables: { quotationId: quotationId } });
        }
      },
    });
  const [updateChecked] = useUpdateQuotationDetailCheckedMutation({
    onCompleted: () => {
      if (param.id) {
        quotationDetailQuery({ variables: { quotationId: param.id } });
      }
    },
  });
  const [show, setShow] = useState(false);
  const [detail, setDetail] = useState<QuotationDetailContentGql | null>(null); // 型名をQuotationDetailContentGqlに変更

  const setSession = () => {
    if (sessionStorage.hasOwnProperty('search-params-temp')) {
      const json = sessionStorage.getItem('search-params-temp');
      if (json) {
        sessionStorage.setItem('search-params', json);
      }
      sessionStorage.removeItem('search-params-temp');
    }
  };

  // useWholesalesQueryフックから卸業者データを取得
  const { data: wholesalesData, loading: wholesalesLoading } = useWholesalesQuery();

  const handleShow = ({ detail: detailToShow }: { detail: QuotationDetailContentGql }) => {
    // detailToShow に変更
    setDetail(detailToShow);
    setShow(true);
  };

  const handleClose = () => {
    setDetail(null);
    setShow(false);
  };

  const handleDelete = () => {
    if (detail === null) {
      handleClose();
      return;
    }
    cancelQuotationDetail({
      variables: { quotationId: detail.quotationId, quotationDetailId: detail.quotationDetailId },
    });
    handleClose();
  };

  useEffect(() => {
    if (param.id) {
      quotationDetailQuery({ variables: { quotationId: param.id } });
    }
  }, [param.id, quotationDetailQuery]);

  return (
    <>
      <Container fluid>
        <Row style={{ marginTop: '3rem', marginBottom: '-2rem' }}>
          <Col className="d-flex justify-content-start align-items-start">
            <Link
              to={QUOTATIONS_PATH}
              className="text-success text-decoration-none"
              onClick={() => setSession()}
            >
              <ChevronLeft className="me-3" />
              依頼履歴一覧に戻る
            </Link>
          </Col>
        </Row>
        {(cancelLoading || loading || wholesalesLoading) && <Loading />}{' '}
        {/* wholesalesLoadingも考慮 */}
      </Container>
      <Container fluid className="mt-5 px-5 pb-5 sheet" style={{ marginBottom: '8rem' }}>
        <Row></Row>
        <Row className="mt-4">
          <Col>
            <div className="fs-3">依頼履歴詳細</div>
          </Col>
        </Row>
        <Row className="mt-4">
          <Col>
            <div className="fs-5 ">
              見積NO. <span className="text-success">{data?.quotation.quotationNo}</span>
            </div>
          </Col>
          <Col></Col>
          <Col className="d-flex justify-content-end">
            <table>
              <tbody>
                <tr>
                  <td style={{ whiteSpace: 'nowrap' }}>依頼日:</td>
                  <td style={{ whiteSpace: 'nowrap' }}>
                    {parseISODateAndformatDate(data?.quotation.requestAt)}
                  </td>
                </tr>
                <tr>
                  <td style={{ whiteSpace: 'nowrap' }}>回答予定日:</td>
                  <td style={{ whiteSpace: 'nowrap' }}>
                    {parseISODateAndformatDate(data?.quotation.answerAt)}
                  </td>
                </tr>
              </tbody>
            </table>
          </Col>
        </Row>

        <hr />

        <Row className="mt-5">
          {/* ↓ 横スクロール対応のための div ラッパー */}
          <div style={{ overflowX: 'auto', width: '100%' }}>
            <Table className="table-borderless">
              <thead className="text-black-50 border-bottom border-success border-3">
                <tr>
                  <th rowSpan={2} className="align-middle text-nowrap">
                    確認
                  </th>
                  <th rowSpan={2} className="align-middle text-nowrap" style={{ width: '3rem' }}>
                    No
                  </th>
                  <th rowSpan={2} className="align-middle text-nowrap" style={{ width: '3rem' }}>
                    区分
                  </th>
                  <th style={{ width: '35rem', whiteSpace: 'nowrap' }}>品名</th>
                  <th rowSpan={2} className="text-nowrap"></th>
                  <th rowSpan={2} className="align-middle text-nowrap" style={{ width: '7rem' }}>
                    薬価
                  </th>
                  <WholesaleNameHeaderCells
                    wholesales={wholesalesData?.wholesales}
                    rowSpan={2}
                    loading={wholesalesLoading}
                  />
                  <th style={{ width: '15rem', whiteSpace: 'nowrap' }}>販売元名</th>
                  <th style={{ width: '10rem', whiteSpace: 'nowrap' }}>JANコード</th>
                  <th style={{ width: '10rem', whiteSpace: 'nowrap' }}>YJコード</th>
                  <th rowSpan={2} className="text-nowrap"></th>
                </tr>
                <tr>
                  <th style={{ whiteSpace: 'nowrap' }}>規格</th>
                  <th style={{ whiteSpace: 'nowrap' }}>指定卸</th>
                  <th style={{ whiteSpace: 'nowrap' }}></th>
                  <th style={{ whiteSpace: 'nowrap' }}>GS1コード</th>
                </tr>
              </thead>
              {data?.quotation.details?.map((detailItem, i) => {
                // detail を detailItem に変更
                return (
                  <tbody
                    key={detailItem.quotationDetailId}
                    className={
                      detailItem.quotationDetailStatus === 'キャンセル' ? 'table-secondary' : ''
                    }
                  >
                    <tr>
                      <td rowSpan={2} className="align-middle text-center">
                        <Form.Check
                          type="checkbox"
                          checked={detailItem.checked === 1}
                          onChange={() =>
                            updateChecked({
                              variables: {
                                quotationId: param.id ?? '',
                                quotationDetailId: detailItem.quotationDetailId,
                                checked: detailItem.checked === 1 ? 0 : 1,
                              },
                            })
                          }
                        />
                      </td>
                      <td rowSpan={2} className="align-middle text-nowrap">
                        {i + 1}
                      </td>
                      <td rowSpan={2} className="align-middle text-nowrap">
                        {detailItem.medicineType}
                      </td>
                      <td style={{ whiteSpace: 'nowrap' }}>
                        <span className="text-success">{detailItem.medicineName}</span>
                      </td>
                      <td rowSpan={2} className="align-middle text-nowrap">
                        {detailItem.quotationDetailStatus === '見積中' && (
                          <Button
                            variant="danger"
                            onClick={() => handleShow({ detail: detailItem })}
                          >
                            キャンセル
                          </Button>
                        )}
                        {detailItem.quotationDetailStatus === 'キャンセル' && (
                          <span className="">キャンセル済</span>
                        )}
                      </td>
                      <td rowSpan={3} className="align-middle text-nowrap">
                        {formatPrice(detailItem.price)}
                      </td>
                      <WholesaleDealPriceCells
                        wholesales={wholesalesData?.wholesales}
                        deals={detailItem.deals}
                      />
                      <td className="border-bottom text-nowrap">{detailItem.salesManufacturer}</td>
                      <td className="border-bottom text-nowrap">{detailItem.janCode}</td>
                      <td className="border-bottom text-nowrap">{detailItem.yjCode}</td>
                    </tr>
                    <tr>
                      <td style={{ whiteSpace: 'nowrap' }}>{detailItem?.capacity}</td>
                      <td style={{ whiteSpace: 'nowrap' }}>{detailItem?.wholesaleName}</td>
                      <td style={{ whiteSpace: 'nowrap' }}>&nbsp;</td>
                      <td style={{ whiteSpace: 'nowrap' }}>{detailItem.gs1Code}</td>
                    </tr>
                    <tr className="border-bottom">
                      <td colSpan={3} className="text-nowrap">
                        &nbsp;
                      </td>
                      {/* detail.note は改行を保持するため text-pre-wrap を維持 */}
                      <td
                        colSpan={1 + 1 + 1 + (wholesalesData?.wholesales?.length || 0) + 1 + 1 + 1}
                        className="text-secondary text-break text-pre-wrap"
                      >
                        {detailItem.note}
                      </td>
                    </tr>
                  </tbody>
                );
              })}
              <tfoot className="border-top border-success border-3">
                <tr>
                  <td>&nbsp;</td>
                </tr>
              </tfoot>
            </Table>
          </div>
        </Row>

        <Row className="fs-5">
          <Table className="table-borderless w-auto">
            <tbody>
              <tr>
                <td className="mb-3 text-nowrap">注文分類</td>
                <td style={{ whiteSpace: 'nowrap' }}>:{data?.quotation.orderStatus}</td>
              </tr>
              <tr>
                <td className="text-nowrap">ご要望など</td>
                {/* ご要望(note)は改行される可能性があるため、nowrapをつけないか、pre-wrapにする */}
                <td style={{ whiteSpace: 'pre-wrap' }}>:{data?.quotation.note}</td>
              </tr>
            </tbody>
          </Table>
        </Row>
      </Container>

      <ConfirmModal
        show={show}
        title="削除確認"
        message={
          <>
            {detail && detail.medicineName}
            <br />
            {detail && detail.capacity}
            <br />
            見積依頼をキャンセルしますか？
            <br />
            この作業は取り消せません。
          </>
        }
        handleOK={handleDelete}
        handleCancel={handleClose}
      />
    </>
  );
};

export default QuotationDetail;
